import { baseApi } from "./baseApi";
import { buildPaginationQueryString } from "./queryHelpers";
import {
  CreateLiveLectureRequest,
  GetLiveLectureStatsQuery,
  GetLiveLecturesInRangeRequest,
  LiveLectureBookingsDetailDto,
  LiveLectureDto,
  LiveLectureInvitationDto,
  LiveLectureMeetingLinkDto,
  LiveLectureSessionActivityDto,
  LiveLectureStatsDto,
  PaginateQuery,
  UpdateLiveLectureRequest,
  AvailableScheduleSlotsRequest,
} from "../../interfaces";
import { MediaDto } from "../../interfaces/media";
import { Paginated } from "../../models";

const serializeDate = (value?: Date | string) =>
  value instanceof Date ? value.toISOString() : value;

const serializeLiveLectureDates = <
  T extends { startAt?: Date | string; endAt?: Date | string },
>(
  data: T,
): T => ({
  ...data,
  ...(data.startAt !== undefined
    ? { startAt: serializeDate(data.startAt) }
    : {}),
  ...(data.endAt !== undefined ? { endAt: serializeDate(data.endAt) } : {}),
});

export const liveLectureApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLiveLectures: builder.query<Paginated<LiveLectureDto>, PaginateQuery>({
      query: (data) => ({
        url: buildPaginationQueryString(data, "/api/live-lecture/session"),
      }),
      providesTags: ["LiveLecture"],
    }),
    getLiveLectureStats: builder.query<
      LiveLectureStatsDto,
      GetLiveLectureStatsQuery | void
    >({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters) {
          Object.entries(filters).forEach(([key, value]) => {
            if (value) params.set(key, value);
          });
        }
        const qs = params.toString();
        return {
          url: qs
            ? `/api/live-lecture/session/stats?${qs}`
            : `/api/live-lecture/session/stats`,
        };
      },
      providesTags: ["LiveLecture"],
    }),
    getLiveLecturesInRange: builder.query<
      LiveLectureDto[],
      GetLiveLecturesInRangeRequest
    >({
      query: (data) => ({
        url: "/api/live-lecture/session/range",
        method: "POST",
        body: data,
      }),
      providesTags: ["LiveLecture"],
    }),
    getLiveLecture: builder.query<LiveLectureDto, string>({
      query: (id) => {
        return { url: `/api/live-lecture/session/${id}` };
      },
      providesTags: (_result, _error, id) => [{ type: "LiveLecture", id }],
    }),
    getLiveLectureSessionActivity: builder.query<
      LiveLectureSessionActivityDto,
      string
    >({
      query: (id) => ({
        url: `/api/live-lecture/session/${id}/session-activity`,
      }),
    }),
    getLiveLectureBookings: builder.query<LiveLectureBookingsDetailDto, string>(
      {
        query: (id) => ({ url: `/api/live-lecture/session/${id}/bookings` }),
        providesTags: (_result, _error, id) => [
          { type: "LiveLecture", id: `${id}-bookings` },
        ],
      },
    ),
    getSessionMeetingLink: builder.query<LiveLectureMeetingLinkDto, string>({
      query: (id) => ({
        url: `/api/live-lecture/session/${id}/meeting-link`,
      }),
    }),
    getBookingMeetingLink: builder.query<LiveLectureMeetingLinkDto, string>({
      query: (bookingId) => ({
        url: `/api/live-lecture/booking/${bookingId}/meeting-link`,
      }),
    }),
    addLiveLecture: builder.mutation<LiveLectureDto, CreateLiveLectureRequest>({
      query: (data) => ({
        url: "/api/live-lecture/session",
        method: "POST",
        body: serializeLiveLectureDates(data),
      }),
      invalidatesTags: ["LiveLecture"],
    }),
    updateLiveLecture: builder.mutation<
      LiveLectureDto,
      { id: string; data: UpdateLiveLectureRequest }
    >({
      query: ({ id, data }) => ({
        url: `/api/live-lecture/session/${id}`,
        method: "PATCH",
        body: serializeLiveLectureDates(data),
      }),
      invalidatesTags: ["LiveLecture"],
    }),
    publishLiveLecture: builder.mutation<LiveLectureDto, string>({
      query: (id) => ({
        url: `/api/live-lecture/session/${id}/publish`,
        method: "PATCH",
      }),
      invalidatesTags: ["LiveLecture"],
    }),
    deleteLiveLecture: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/live-lecture/session/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["LiveLecture"],
    }),
    uploadLiveLectureImage: builder.mutation<
      MediaDto,
      {
        file: File;
        liveLectureId?: string | null;
        onProgress?: (progress: number) => void;
      }
    >({
      query: ({ file, liveLectureId, onProgress }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: liveLectureId
            ? `/api/live-lecture/session/upload/image?id=${liveLectureId}`
            : `/api/live-lecture/session/upload/image`,
          method: "POST",
          data: formData,
          onUploadProgress: (event: any) => {
            if (event.total && onProgress) {
              onProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        };
      },
      invalidatesTags: ["LiveLecture"],
    }),
    uploadLiveLectureNotes: builder.mutation<
      MediaDto,
      {
        file: File;
        liveLectureId?: string | null;
        onProgress?: (progress: number) => void;
      }
    >({
      query: ({ file, liveLectureId, onProgress }) => {
        const formData = new FormData();
        formData.append("file", file);

        return {
          url: liveLectureId
            ? `/api/live-lecture/session/upload/notes?id=${liveLectureId}`
            : `/api/live-lecture/session/upload/notes`,
          method: "POST",
          data: formData,
          onUploadProgress: (event: any) => {
            if (event.total && onProgress) {
              onProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        };
      },
      invalidatesTags: ["LiveLecture"],
    }),
    deleteLiveLectureMedia: builder.mutation<
      void,
      { liveLectureId: string; mediaId: string }
    >({
      query: ({ liveLectureId, mediaId }) => ({
        url: `/api/live-lecture/session/${liveLectureId}/media/${mediaId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["LiveLecture"],
    }),
    getScheduleAvailableSlots: builder.mutation<
      LiveLectureDto[],
      AvailableScheduleSlotsRequest
    >({
      query: (body) => ({
        url: "/api/live-lecture/session/schedule/available-slots",
        method: "POST",
        body: {
          ...body,
          currentTime: body.currentTime || new Date().toISOString(),
        },
      }),
    }),
    getLiveLectureInvitations: builder.query<LiveLectureInvitationDto[], string>(
      {
        query: (id) => ({
          url: `/api/live-lecture/session/${id}/invitations`,
        }),
        providesTags: (_result, _error, id) => [
          { type: "LiveLecture", id: `${id}-invitations` },
        ],
      },
    ),
    createLiveLectureInvitation: builder.mutation<
      LiveLectureInvitationDto,
      string
    >({
      query: (id) => ({
        url: `/api/live-lecture/session/${id}/invitations`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "LiveLecture", id: `${id}-invitations` },
      ],
    }),
    getPresenceWatch: builder.mutation<
      LiveLecturePresenceWatchDto,
      { ids: string[] }
    >({
      query: (body) => ({
        url: "/api/live-lecture/session/presence-watch",
        method: "POST",
        body,
      }),
    }),
  }),
});

export type LiveLecturePresenceWatchDto = {
  callingUrl: string;
  namespace: string;
  token: string;
  expiresIn: string;
  rooms: { liveLectureId: string; roomId: string }[];
};

export const {
  useGetLiveLecturesQuery,
  useGetLiveLectureStatsQuery,
  useLazyGetLiveLecturesInRangeQuery,
  useGetLiveLectureQuery,
  useGetLiveLectureBookingsQuery,
  useLazyGetLiveLectureSessionActivityQuery,
  useLazyGetSessionMeetingLinkQuery,
  useLazyGetBookingMeetingLinkQuery,
  useAddLiveLectureMutation,
  useUpdateLiveLectureMutation,
  usePublishLiveLectureMutation,
  useDeleteLiveLectureMutation,
  useUploadLiveLectureImageMutation,
  useUploadLiveLectureNotesMutation,
  useDeleteLiveLectureMediaMutation,
  useGetScheduleAvailableSlotsMutation,
  useGetLiveLectureInvitationsQuery,
  useCreateLiveLectureInvitationMutation,
  useGetPresenceWatchMutation,
} = liveLectureApi;
